import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'

export interface User {
  id: string
  name: string
  last_name: string | null
  middle_name: string | null
  full_name: string
  email: string
  avatar: string | null
  avatar_url: string | null
  birth_date: string | null
  initials: string
  locale: string
  theme: string
  is_admin: boolean
  created_at: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  name: string
  last_name: string
  middle_name?: string
  email: string
  password: string
  password_confirmation: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const isImpersonating = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)

  // Actions
  async function fetchUser() {
    try {
      isLoading.value = true
      const response = await api.get('/auth/user')
      user.value = response.data.data
      isImpersonating.value = response.data.impersonating || false
    } catch {
      user.value = null
      isImpersonating.value = false
    } finally {
      isLoading.value = false
      isInitialized.value = true
    }
  }

  async function login(credentials: LoginCredentials) {
    isLoading.value = true
    try {
      await api.post('/auth/login', credentials)
      await fetchUser()
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Login failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function register(data: RegisterData) {
    isLoading.value = true
    try {
      await api.post('/auth/register', data)
      await fetchUser()
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Registration failed',
        errors: err.response?.data?.errors || {},
      }
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      user.value = null
    }
  }

  async function updateProfile(data: Partial<User>) {
    isLoading.value = true
    try {
      const response = await api.put('/profile', data)
      user.value = response.data.data
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Update failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string, newPasswordConfirmation: string) {
    isLoading.value = true
    try {
      await api.put('/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirmation,
      })
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Password change failed',
        errors: err.response?.data?.errors || {},
      }
    } finally {
      isLoading.value = false
    }
  }

  async function uploadAvatar(file: File) {
    isLoading.value = true
    try {
      const formData = new FormData()
      formData.append('avatar', file)
      const response = await api.post('/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      user.value = response.data.data
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Upload failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function deleteAvatar() {
    isLoading.value = true
    try {
      await api.delete('/profile/avatar')
      if (user.value) {
        user.value.avatar = null
        user.value.avatar_url = null
      }
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Delete failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  function setUser(newUser: User) {
    user.value = newUser
  }

  async function impersonate(userId: string) {
    isLoading.value = true
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`)
      user.value = response.data.data
      isImpersonating.value = true
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Impersonation failed',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function stopImpersonate() {
    isLoading.value = true
    try {
      const response = await api.post('/stop-impersonate')
      user.value = response.data.data
      isImpersonating.value = false
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to stop impersonation',
      }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    user,
    isInitialized,
    isLoading,
    isImpersonating,
    // Getters
    isAuthenticated,
    // Actions
    fetchUser,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    uploadAvatar,
    deleteAvatar,
    setUser,
    impersonate,
    stopImpersonate,
  }
})
